import { Trans, useLingui } from "@lingui/react/macro";
import {
  isGroup as isGroupCheck,
  isEmployeePerformed,
  timeOfDaySchema,
  type IActivityOccurrenceOrGroup,
  groupStatusSchema,
  getGroupOrVisitStatus,
  activityOccurrenceStatusSchema,
} from "@medoma/models/activities";
import clsx from "clsx";
import { dateName } from "@/Utils/DateUtils";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import { Heading } from "@medoma/components/Heading/Heading";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import styles from "./ActivitiesList.module.scss";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { employeeName } from "@medoma/models/shifts";
import { useUserProfile } from "@/api/Users";
import { ActionCardLink } from "@/components/ActionCard/ActionCard";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { Text } from "@medoma/components/Text/Text";
import { activityCategoryDictionary } from "@/api/Activities";

export const ActivitiesList = ({
  activityOccurrencesAndGroups,
}: {
  activityOccurrencesAndGroups: IActivityOccurrenceOrGroup[];
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();

  const activitiesByDate = activityOccurrencesAndGroups.reduce(
    (acc, curr) => {
      const dateKey = machineFormat(curr.start, "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(curr);
      return acc;
    },
    {} as Record<string, typeof activityOccurrencesAndGroups>,
  );

  const sortedDates = Object.keys(activitiesByDate).sort();

  return (
    <ol aria-label={t`Lista med aktiviteter`} className={styles.activities}>
      {sortedDates.map((date) => {
        const activities = activitiesByDate[date];

        if (!activities || activities.length === 0) {
          return <Trans key={date}>Inga aktiviteter denna dag</Trans>;
        }

        const day = calendarDateSchema.decode(date);
        const nameOfDay = dateName(day);

        return (
          <li key={date}>
            <Heading level="h2">{`${nameOfDay} (${activities.length})`}</Heading>

            <ol className={styles.activitiesForDate}>
              {activities.map((activityOccurrenceOrGroup) => {
                const isGroup = isGroupCheck(activityOccurrenceOrGroup);
                const category = activityOccurrenceOrGroup.category;
                // A group without a visitStatus reads as notReady ("Att
                // planera") — mirrors what individual occurrences already
                // surface in the same not-yet-planned state.
                const status = isGroup
                  ? (getGroupOrVisitStatus(activityOccurrenceOrGroup) ??
                    activityOccurrenceStatusSchema.enum.notReady)
                  : activityOccurrenceOrGroup.status;
                const isCancelledVisit =
                  isGroup &&
                  activityOccurrenceOrGroup.visitStatus ===
                    groupStatusSchema.enum.cancelled;

                return (
                  <li
                    key={
                      isGroup
                        ? activityOccurrenceOrGroup.id
                        : `${activityOccurrenceOrGroup.activityId}-${activityOccurrenceOrGroup.id}`
                    }
                    className={clsx(isCancelledVisit && styles.cancelled)}
                  >
                    <ActionCardLink
                      to={
                        isGroup
                          ? `groups/${activityOccurrenceOrGroup.id}`
                          : `${activityOccurrenceOrGroup.activityId}/occurrences/${activityOccurrenceOrGroup.id}`
                      }
                      title={
                        isGroup
                          ? t(activityCategoryDictionary[category])
                          : activityOccurrenceOrGroup.title
                      }
                      icon={<CategoryIcon category={category} />}
                    >
                      <div className={styles.timeAndAssignees}>
                        <TimeSpan
                          size="base"
                          timespan={
                            activityOccurrenceOrGroup.timeOfDay ===
                            timeOfDaySchema.enum.Any
                              ? activityOccurrenceOrGroup.timeOfDay
                              : {
                                  start: activityOccurrenceOrGroup.start,
                                  end: activityOccurrenceOrGroup.end,
                                }
                          }
                        />
                        <StatusTag
                          status={status}
                          variant="icon-and-text"
                          size="small"
                        />
                        {isEmployeePerformed(activityOccurrenceOrGroup) &&
                        activityOccurrenceOrGroup.assignees.some(
                          (shift) => shift.employee,
                        ) ? (
                          <div className={styles.assignees}>
                            {activityOccurrenceOrGroup.assignees
                              .filter((shift) => shift.employee)
                              .map((shift) => (
                                <CompetenceChip
                                  key={shift.id}
                                  competence={shift.competence}
                                  state="neutral"
                                  content={
                                    shift.employee
                                      ? employeeName(shift.employee, {
                                          countryCode,
                                          viewer: "colleague",
                                        })
                                      : ""
                                  }
                                />
                              ))}
                          </div>
                        ) : "requiredCompetences" in
                            activityOccurrenceOrGroup &&
                          activityOccurrenceOrGroup.requiredCompetences.length >
                            0 ? (
                          <div className={styles.assignees}>
                            {activityOccurrenceOrGroup.requiredCompetences.map(
                              (competence) => (
                                <CompetenceChip
                                  key={competence}
                                  competence={competence}
                                  state="unassigned"
                                />
                              ),
                            )}
                          </div>
                        ) : null}
                      </div>
                      {!isGroup ? (
                        <div className={styles.descriptionAndNote}>
                          {activityOccurrenceOrGroup.description ? (
                            <Text element="span" size="small" color="faded">
                              {activityOccurrenceOrGroup.description}
                            </Text>
                          ) : null}
                          {activityOccurrenceOrGroup.note ? (
                            <Text element="span" size="small" color="faded">
                              {activityOccurrenceOrGroup.note}
                            </Text>
                          ) : null}
                        </div>
                      ) : null}
                      {isGroup && (
                        <ul className={styles.groupActivities}>
                          {activityOccurrenceOrGroup.occurrences.map(
                            (occurrence) => {
                              return (
                                <li
                                  key={`${occurrence.activityId}-${occurrence.id}`}
                                >
                                  <ActivityTitle
                                    activityOccurrence={occurrence}
                                    size="small"
                                    showStatus
                                  />
                                </li>
                              );
                            },
                          )}
                        </ul>
                      )}
                    </ActionCardLink>
                  </li>
                );
              })}
            </ol>
          </li>
        );
      })}
    </ol>
  );
};
